/* eslint-disable react/no-unknown-property */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowRight, Boxes, Factory, ShoppingBag, Cpu, BarChart3, Cloud, Wallet, Wrench, Sparkles as SparklesIcon, ScanLine, Globe2, Building2, Network, Store, User } from "lucide-react";
import TileSphereLogo from "@/components/TileSphereLogo";
import RoleHierarchyExplorer from "@/components/RoleHierarchyExplorer";
import LanguageSwitcher from "@/components/LanguageSwitcher";

// =================== HERO (CSS/SVG cinematic ecosystem) ===================

const HERO_MODULES = [
  { labelKey: "organization", icon: Building2, color: "from-emerald-400 to-teal-600", to: "/portals/organization" },
  { labelKey: "distributor", icon: Network, color: "from-blue-400 to-indigo-600", to: "/portals/distributor" },
  { labelKey: "vendor", icon: ShoppingBag, color: "from-pink-400 to-fuchsia-600", to: "/portals/vendor" },
  { labelKey: "dealer", icon: Store, color: "from-amber-400 to-orange-600", to: "/login" },
  { labelKey: "customer", icon: User, color: "from-cyan-400 to-sky-600", to: "/login" },
];

function HeroEcosystem() {
  const { t } = useTranslation();
  const R = 240; // orbit radius
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      {/* glow backdrop */}
      <div className="absolute w-[600px] h-[600px] rounded-full bg-orange-500/10 blur-3xl" />
      <div className="absolute w-[400px] h-[400px] rounded-full bg-pink-500/10 blur-2xl" />

      {/* orbit ring */}
      <motion.div
        className="absolute rounded-full border border-white/10"
        style={{ width: R * 2, height: R * 2 }}
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute rounded-full border border-orange-400/15 border-dashed"
        style={{ width: R * 2 + 40, height: R * 2 + 40 }}
        animate={{ rotate: -360 }}
        transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
      />

      {/* core sphere with 3D look */}
      <div className="absolute" style={{ transformStyle: "preserve-3d" }}>
        <motion.div
          animate={{ rotateY: 360 }}
          transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
          className="relative w-44 h-44 rounded-full bg-gradient-to-br from-orange-400 via-pink-500 to-fuchsia-700 shadow-[0_0_120px_rgba(255,99,71,0.5)]"
        >
          <div className="absolute top-4 left-6 w-16 h-16 rounded-full bg-white/40 blur-2xl" />
          <div className="absolute inset-0 rounded-full" style={{ background: "repeating-linear-gradient(45deg, transparent 0 14px, rgba(255,255,255,0.06) 14px 15px), repeating-linear-gradient(-45deg, transparent 0 14px, rgba(255,255,255,0.06) 14px 15px)" }} />
          <div className="absolute inset-0 rounded-full border border-white/20" />
        </motion.div>
        {/* static CORE label - not affected by rotation */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-[10px] uppercase tracking-[0.35em] text-white/90 font-bold drop-shadow-md">{t("landing.core")}</span>
        </div>
      </div>

      {/* connecting lines (SVG, drawn from center to each module slot) */}
      <svg className="absolute" width={R * 2 + 100} height={R * 2 + 100} style={{ overflow: "visible" }}>
        <defs>
          <linearGradient id="ll" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ff8a3d" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#ff8a3d" stopOpacity="0" />
          </linearGradient>
        </defs>
        {HERO_MODULES.map((_, i) => {
          const a = (i / HERO_MODULES.length) * Math.PI * 2 - Math.PI / 2;
          const cx = R + 50;
          const cy = R + 50;
          const x2 = cx + Math.cos(a) * R;
          const y2 = cy + Math.sin(a) * R;
          return <line key={i} x1={cx} y1={cy} x2={x2} y2={y2} stroke="url(#ll)" strokeWidth="1" />;
        })}
      </svg>

      {/* orbiting modules — single rotating container with counter-rotated labels */}
      <motion.div
        className="absolute"
        animate={{ rotate: 360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        style={{ width: R * 2, height: R * 2 }}
      >
        {HERO_MODULES.map((m, i) => {
          const baseAngle = (i / HERO_MODULES.length) * 360;
          return (
            <div
              key={m.labelKey}
              className="absolute top-1/2 left-1/2"
              style={{ transform: `rotate(${baseAngle}deg) translate(${R}px) rotate(-${baseAngle}deg)`, transformOrigin: "0 0" }}
            >
              <Link to={m.to}>
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                  whileHover={{ scale: 1.15 }}
                  className="flex flex-col items-center cursor-pointer gap-2 pointer-events-auto"
                  style={{ marginLeft: -32, marginTop: -32 }}
                  data-testid={`hero-module-${m.labelKey}`}
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${m.color} flex items-center justify-center shadow-2xl shadow-black/40 border border-white/20 backdrop-blur-xl`}>
                    <m.icon className="w-7 h-7 text-white drop-shadow-md" />
                  </div>
                  <div className="whitespace-nowrap px-2 py-0.5 rounded-full bg-black/60 backdrop-blur border border-white/10 text-[11px] font-bold text-white/90">
                    {t(`landing.modules.${m.labelKey}`)}
                  </div>
                </motion.div>
              </Link>
            </div>
          );
        })}
      </motion.div>

      {/* particles */}
      {Array.from({ length: 30 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full bg-orange-300/60"
          style={{ left: `${50 + (Math.random() - 0.5) * 60}%`, top: `${50 + (Math.random() - 0.5) * 60}%` }}
          animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1.4, 0.8] }}
          transition={{ duration: 3 + Math.random() * 4, repeat: Infinity, delay: Math.random() * 3 }}
        />
      ))}
    </div>
  );
}

// =================== PLATFORM OVERVIEW ===================
const PLATFORM_MODULES = [
  { labelKey: "manufacturing", icon: Factory, color: "from-orange-500/30 to-orange-700/10" },
  { labelKey: "inventory", icon: Boxes, color: "from-emerald-500/30 to-emerald-700/10" },
  { labelKey: "warehouse", icon: ScanLine, color: "from-violet-500/30 to-violet-700/10" },
  { labelKey: "sales", icon: ShoppingBag, color: "from-pink-500/30 to-pink-700/10" },
  { labelKey: "crm", icon: BarChart3, color: "from-cyan-500/30 to-cyan-700/10" },
  { labelKey: "marketplace", icon: Globe2, color: "from-amber-500/30 to-amber-700/10" },
  { labelKey: "finance", icon: Wallet, color: "from-blue-500/30 to-blue-700/10" },
  { labelKey: "production", icon: Wrench, color: "from-rose-500/30 to-rose-700/10" },
  { labelKey: "analytics", icon: BarChart3, color: "from-indigo-500/30 to-indigo-700/10" },
  { labelKey: "iotReady", icon: Cpu, color: "from-teal-500/30 to-teal-700/10" },
  { labelKey: "cloud", icon: Cloud, color: "from-slate-500/30 to-slate-700/10" },
];

function PlatformOverview() {
  const { t } = useTranslation();
  return (
    <section className="relative py-28 px-6" id="platform">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
          <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">{t("landing.platformKicker")}</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-3 leading-[1.05] text-white">
            {t("landing.platformTitle1")} <br /> {t("landing.platformTitle2")}
          </h2>
          <p className="mt-5 text-white/60 max-w-2xl">{t("landing.platformSub")}</p>
        </motion.div>

        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {PLATFORM_MODULES.map((m, i) => (
            <motion.div
              key={m.labelKey}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.5 }}
              viewport={{ once: true }}
              whileHover={{ y: -6, scale: 1.02 }}
              className={`group relative rounded-2xl border border-white/10 bg-gradient-to-br ${m.color} backdrop-blur-xl p-6 overflow-hidden shadow-lg`}
              data-testid={`platform-card-${m.labelKey}`}
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.1),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity" />
              <m.icon className="w-7 h-7 text-white/90 mb-4" />
              <div className="font-display text-xl font-bold text-white">{t(`landing.modules.${m.labelKey}`)}</div>
              <div className="text-xs text-white/50 mt-1">{t("landing.configurable")}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =================== PORTAL CARDS ===================
const PORTAL_CARDS = [
  { code: "ORGANIZATION", titleKey: "landing.portals.orgTitle", taglineKey: "landing.portals.orgTagline", color: "from-emerald-600 to-emerald-900", to: "/portals/organization", icon: Factory, featureKeys: ["landing.portals.orgF1", "landing.portals.orgF2", "landing.portals.orgF3", "landing.portals.orgF4"] },
  { code: "DISTRIBUTOR", titleKey: "landing.portals.distTitle", taglineKey: "landing.portals.distTagline", color: "from-blue-600 to-indigo-900", to: "/portals/distributor", icon: Globe2, featureKeys: ["landing.portals.distF1", "landing.portals.distF2", "landing.portals.distF3", "landing.portals.distF4"] },
  { code: "VENDOR", titleKey: "landing.portals.vendorTitle", taglineKey: "landing.portals.vendorTagline", color: "from-pink-600 to-fuchsia-900", to: "/portals/vendor", icon: ShoppingBag, featureKeys: ["landing.portals.vendorF1", "landing.portals.vendorF2", "landing.portals.vendorF3", "landing.portals.vendorF4"] },
];

function PortalSelector() {
  const { t } = useTranslation();
  return (
    <section className="relative py-28 px-6 bg-black" id="portals">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
          <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">{t("landing.portalsKicker")}</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-3 text-white leading-[1.05]">
            {t("landing.portalsTitle1")} <br /> {t("landing.portalsTitle2")}
          </h2>
          <p className="mt-5 text-white/60 max-w-2xl">{t("landing.portalsSub")}</p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6 mt-14">
          {PORTAL_CARDS.map((p, i) => (
            <motion.div
              key={p.code}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className="relative rounded-3xl overflow-hidden border border-white/10 group"
              data-testid={`portal-card-${p.code.toLowerCase()}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${p.color}`} />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_0%,rgba(255,255,255,0.15),transparent_60%)]" />
              <div className="relative p-8 flex flex-col h-full min-h-[420px]">
                <p.icon className="w-10 h-10 text-white" />
                <h3 className="font-display text-2xl sm:text-3xl font-black mt-6 text-white">{t(p.titleKey)}</h3>
                <p className="text-white/70 mt-2">{t(p.taglineKey)}</p>
                <ul className="mt-6 space-y-2 flex-1">
                  {p.featureKeys.map((fk) => (
                    <li key={fk} className="text-sm text-white/80 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-white/60 mt-2" /> {t(fk)}
                    </li>
                  ))}
                </ul>
                <Link to={p.to} className="mt-8">
                  <Button className="w-full bg-white text-black hover:bg-white/90 rounded-xl font-semibold" data-testid={`portal-cta-${p.code.toLowerCase()}`}>
                    {t("landing.explorePortal")} <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =================== ROOT ===================
export default function Landing() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-[#0a0a14] text-white overflow-x-hidden" data-testid="landing-page">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/30 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <TileSphereLogo />
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#platform" className="hover:text-white">{t("landing.navPlatform")}</a>
            <a href="#hierarchy" className="hover:text-white">{t("landing.navHierarchy")}</a>
            <a href="#portals" className="hover:text-white">{t("landing.navPortals")}</a>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="dark" />
            <Link to="/login"><Button variant="ghost" className="text-white hover:bg-white/10" data-testid="nav-login-btn">{t("common.signIn")}</Button></Link>
            <Link to="/subscribe"><Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full" data-testid="nav-register-btn">{t("common.register")} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative h-[100vh] min-h-[750px] bg-gradient-to-br from-[#0a0a14] via-[#150a1f] to-[#0a0a14]" data-testid="hero-3d">
        <HeroEcosystem />
        <div className="absolute inset-x-0 bottom-0 px-6 pb-16 pointer-events-none">
          <div className="max-w-7xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }} className="pointer-events-auto max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/15 text-[11px] font-bold uppercase tracking-[0.25em] text-orange-300">
                <SparklesIcon className="w-3 h-3" /> {t("landing.heroTag")}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black mt-4 leading-[0.95]">
                {t("landing.heroTitleLine1")} <br />
                <span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">{t("landing.heroTitleLine2")}</span>
              </h1>
              <p className="mt-5 text-white/70 max-w-xl">{t("landing.heroSub")}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <PlatformOverview />

      <section className="py-28 px-6 bg-gradient-to-b from-[#0a0a14] to-black" id="hierarchy">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">{t("landing.hierarchyKicker")}</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-3 leading-[1.05]">
              {t("landing.hierarchyTitle1")} <br />{t("landing.hierarchyTitle2")}
            </h2>
            <p className="mt-5 text-white/60 max-w-2xl">{t("landing.hierarchySub")}</p>
          </motion.div>
          <div className="mt-12">
            <RoleHierarchyExplorer />
          </div>
        </div>
      </section>

      {/* Compact 3-portal CTA — full plans + role picker live on /subscribe */}
      <section className="relative py-24 px-6 bg-black" id="portals">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">{t("landing.portalsKicker")}</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black mt-3 text-white leading-[1.05]">
            One ecosystem. <br /><span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">Three portals.</span>
          </h2>
          <p className="mt-4 text-white/60 max-w-2xl">Pick your portal — Organization, Vendor or Distributor — and unlock the plans curated for you on the Subscription page.</p>
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link to="/subscribe"><Button className="h-12 px-6 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-bold" data-testid="landing-subscribe-cta">See plans <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
            <Link to="/subscribe" className="text-white/70 hover:text-white text-sm underline">Compare all plans →</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-12 px-6 bg-black text-white/50 text-sm">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <TileSphereLogo subtle />
          <div>{t("landing.footerRights")}</div>
          <div className="flex gap-5">
            <Link to="/login" className="hover:text-white">{t("common.signIn")}</Link>
            <Link to="/subscribe" className="hover:text-white">{t("common.register")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
